import { clsx, type ClassValue } from "clsx";

export const cn = (...v: ClassValue[]) => clsx(v);

export { coupleTitle, initials } from "@bridewell/shared";

/** Client-side downscale before upload so R2 stores one display-ready image. */
export async function downscaleImage(file: File, maxEdge = 1600, quality = 0.86): Promise<{ blob: Blob; width: number; height: number }> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return { blob: file, width: 0, height: 0 };
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", quality));
  return { blob: blob ?? file, width: w, height: h };
}

export function toLocalDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function toLocalDateTimeInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${toLocalDateInput(iso)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
/** `<input type=date>` value → ISO at local noon (avoids day-shift across time zones). */
export function fromDateInput(v: string): string | null {
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y!, m! - 1, d!, 12, 0, 0).toISOString();
}
export function fromDateTimeInput(v: string): string | null {
  if (!v) return null;
  return new Date(v).toISOString();
}

/**
 * Keeps a `?redirect=` target on this origin: a path that starts with a single "/" and carries no
 * scheme. Anything else (absolute URLs, protocol-relative "//host", "javascript:") is dropped so a
 * crafted sign-in link can never bounce someone to another site.
 */
export function safeInternalPath(v: string | undefined | null): string | undefined {
  if (!v) return undefined;
  if (!v.startsWith("/") || v.startsWith("//") || v.startsWith("/\\")) return undefined;
  if (/^\/[^/?#]*:/.test(v)) return undefined;
  return v;
}

/**
 * Public token pages (guest RSVP links, shared run sheets, the guest-mail opt-out): no session, no service worker, no cookie notice, nothing
 * stored on the visitor's device. The web Worker adds noindex / no-store / no-referrer for the same prefixes.
 */
export const TOKEN_PAGE_PREFIXES = ["/rsvp/", "/s/", "/mail-opt-out"] as const;
export const isTokenPage = (pathname: string) => TOKEN_PAGE_PREFIXES.some((p) => pathname.startsWith(p));

/** Privacy boundary, not a consent/banner rule: account invites also carry bearer credentials. */
export const isBearerLinkPage = (pathname: string) => isTokenPage(pathname) || pathname.startsWith("/join/");

/**
 * Auth round trips carry the invite in `redirect`, not in the pathname. Inspect `next` too, but never
 * navigate to these values. URLSearchParams decodes once; bounded recursion handles nested auth redirects.
 * Malformed encoding is treated conservatively (suppress analytics), never thrown into the UI.
 */
export function isBearerLinkLocation(location: { pathname: string; search: string }, depth = 0): boolean {
  if (isBearerLinkPage(location.pathname)) return true;
  const search = new URLSearchParams(location.search);
  for (const key of ["redirect", "next"]) {
    for (const value of search.getAll(key)) {
      if (!value) continue;
      if (depth >= 4) return true;
      let decoded = value;
      try {
        // A second encoded layer can occur when a return URL itself is carried by another URL.
        if (decoded.includes("%")) decoded = decodeURIComponent(decoded);
        const target = new URL(decoded, "https://bearer-check.invalid");
        if (isBearerLinkLocation(target, depth + 1)) return true;
      } catch {
        return true;
      }
    }
  }
  return false;
}
